import React, { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player/lazy';

// Only the Radio embedding provides this owner. Ordinary callers retain all
// ReactPlayer defaults and existing behavior; no provider-specific SDK is added.
export const GalaxyChatMediaContext = createContext(null);

function ScopedPlayer({ owner, ...props }) {
  const element = useRef(null);
  const alive = useRef(false);
  const [playing, setPlaying] = useState(false);
  const controller = useMemo(() => ({
    contains: (media) => element.current?.contains(media),
    pause: () => { if (alive.current) setPlaying(false); },
  }), []);
  useLayoutEffect(() => {
    alive.current = true;
    const release = owner.register(controller);
    return () => { alive.current = false; release(); };
  }, [owner, controller]);
  useLayoutEffect(() => {
    // Commit the SDK's observed play before rejecting it. ReactPlayer 2 pauses
    // on true -> false; leaving playing=false after an iframe click cannot stop it.
    if (playing && !owner.requestPlay(controller)) setPlaying(false);
  }, [playing, owner, controller]);
  return <div ref={element} data-chat-media-player style={{ display: 'contents' }}>
    <ReactPlayer {...props} playing={playing} stopOnUnmount
      onPlay={() => { if (alive.current) setPlaying(true); }}
      onPause={() => { if (alive.current) setPlaying(false); }}
      onEnded={() => { if (alive.current) setPlaying(false); }} />
  </div>;
}

export default function GalaxyChatMedia(props) {
  const owner = useContext(GalaxyChatMediaContext);
  return owner ? <ScopedPlayer {...props} key={props.url} owner={owner} /> : <ReactPlayer {...props} />;
}
